import React, { useState, useEffect, useRef } from "react";

type Difficulty = 5 | 7 | 9;

const DIFFICULTIES: { label: string; size: Difficulty }[] = [
  { label: "5x5 (Mudah)", size: 5 },
  { label: "7x7 (Sedang)", size: 7 },
  { label: "9x9 (Sulit)", size: 9 },
];

function randomGrid(size: number) {
  return Array.from({ length: size }, () =>
    Array.from({ length: size }, () => Math.random() > 0.5)
  );
}

export const LightsOut: React.FC = () => {
  const [size, setSize] = useState<Difficulty>(5);
  const [grid, setGrid] = useState<boolean[][]>(() => randomGrid(5));
  const [moves, setMoves] = useState(0);
  const [time, setTime] = useState(0);
  const [timerOn, setTimerOn] = useState(true);
  const [hintsLeft, setHintsLeft] = useState(3);
  const [message, setMessage] = useState("");
  const timerRef = useRef<number | null>(null);

  // For animating hint
  const [hintCell, setHintCell] = useState<{ row: number; col: number } | null>(null);

  useEffect(() => {
    if (!timerOn) return;
    timerRef.current = window.setInterval(() => {
      setTime((t) => t + 1);
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [timerOn]);

  const createNewGame = (newSize: Difficulty) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setSize(newSize);
    setGrid(randomGrid(newSize));
    setMoves(0);
    setTime(0);
    setMessage("");
    setHintsLeft(3);
    setHintCell(null);
    setTimerOn(true);
  };

  // Cell toggling logic
  const toggleCell = (row: number, col: number) => {
    setGrid((prevGrid) => {
      const next = prevGrid.map((r) => [...r]);
      const dirs = [
        [0, 0],
        [0, 1],
        [0, -1],
        [1, 0],
        [-1, 0],
      ];
      for (let [dx, dy] of dirs) {
        const r = row + dx, c = col + dy;
        if (r >= 0 && r < size && c >= 0 && c < size) {
          next[r][c] = !next[r][c];
        }
      }
      return next;
    });
    setMoves((m) => m + 1);
    setMessage("");
  };

  // Win check
  useEffect(() => {
    const allOff = grid.flat().every((v) => !v);
    if (allOff) {
      if (timerRef.current) clearInterval(timerRef.current);
      setTimerOn(false);
      setMessage(`Menang dalam ${moves} langkah dan ${time} detik!`);
    }
  // eslint-disable-next-line
  }, [grid]);

  // Hint logic
  const giveHint = () => {
    if (hintsLeft <= 0) return;
    const onCells: { r: number; c: number }[] = [];
    grid.forEach((row, r) =>
      row.forEach((val, c) => {
        if (val) onCells.push({ r, c });
      })
    );
    // If any light is on, highlight a random on cell, else random cell
    let row: number, col: number;
    if (onCells.length > 0) {
      const idx = Math.floor(Math.random() * onCells.length);
      row = onCells[idx].r;
      col = onCells[idx].c;
    } else {
      row = Math.floor(Math.random() * size);
      col = Math.floor(Math.random() * size);
    }
    setHintCell({ row, col });
    setHintsLeft((hl) => hl - 1);
    setTimeout(() => setHintCell(null), 900);
  };

  const restart = () => createNewGame(size);

  // Custom styling inline for React version, matching original
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        backgroundColor: "#2c3e50",
        color: "white",
        fontFamily: "Arial, sans-serif",
        minHeight: "100vh",
        width: "100vw",
        paddingBottom: 40,
      }}
    >
      <h1 style={{ margin: 20, fontWeight: 800, letterSpacing: 2, fontSize: 36 }}>LIGHTS OUT</h1>

      <div className="difficulty" style={{ margin: 20 }}>
        {DIFFICULTIES.map((dif) => (
          <button
            key={dif.size}
            style={{
              padding: "10px 20px",
              margin: "5px",
              border: "none",
              borderRadius: 5,
              background: size === dif.size ? "#16a085" : "#27ae60",
              color: "white",
              cursor: "pointer",
              transition: "background 0.2s",
              fontWeight: size === dif.size ? "bold" : "normal",
            }}
            onClick={() => createNewGame(dif.size)}
            disabled={size === dif.size}
          >
            {dif.label}
          </button>
        ))}
      </div>

      <div className="game-container" style={{ margin: 20, position: "relative" }}>
        <div
          className="grid"
          style={{
            display: "grid",
            gridGap: 5,
            padding: 10,
            borderRadius: 10,
            background: "#34495e",
            gridTemplateColumns: `repeat(${size}, 50px)`,
            justifyContent: "center",
          }}
        >
          {grid.map((row, r) =>
            row.map((isOn, c) => (
              <div
                key={`${r}-${c}`}
                className={`cell${isOn ? " on" : ""}`}
                data-row={r}
                data-col={c}
                onClick={() => {
                  if (message) return;
                  toggleCell(r, c);
                }}
                style={{
                  width: 50,
                  height: 50,
                  background: isOn ? "#f1c40f" : "#2c3e50",
                  borderRadius: 5,
                  boxShadow: isOn ? "0 0 15px #f1c40f" : undefined,
                  transition: "all 0.3s",
                  cursor: "pointer",
                  outline:
                    hintCell && hintCell.row === r && hintCell.col === c
                      ? "3px solid #00ffbb"
                      : undefined,
                  transform:
                    hintCell && hintCell.row === r && hintCell.col === c
                      ? "scale(1.18)"
                      : "scale(1)",
                  zIndex: hintCell && hintCell.row === r && hintCell.col === c ? 2 : 1,
                }}
              />
            ))
          )}
        </div>
      </div>

      <div className="stats" style={{ margin: 20, textAlign: "center" }}>
        <div>Langkah: <span id="moves">{moves}</span></div>
        <div>Waktu: <span id="time">{time}</span> detik</div>
        <div id="message" style={{ height: 30, margin: 10, color: "#e67e22", fontWeight: 600 }}>
          {message || null}
        </div>
        
        <button
          onClick={restart}
          style={{
            padding: "10px 20px",
            margin: 5,
            border: "none",
            borderRadius: 5,
            backgroundColor: "#27ae60",
            color: "white",
            cursor: "pointer",
          }}
        >
          Ulangi
        </button>
      </div>
    </div>
  );
};

